import * as React from 'react';
import { PlusCircledIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Formik, Form } from 'formik';
import { FormControl, FormItem, FormLabel } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { formatIDR, extractNumber, formatCurrency } from '@/lib/utils';
import DatePicker from '@/components/commons/date-picker';
import { format } from 'date-fns';

interface DataTableFacetedFormProps<T> {
  title?: string;
  options: {
    label: string;
    value: T;
    valValue?: string;
    mode: 'rupiah' | 'text' | 'number' | 'date';
  }[];
  onApply?: (values: { value: T; valValue: string }[] | undefined) => void;
}

export function DataTableFacetedForm<T extends string | number>({
  title,
  options = [],
  onApply,
}: DataTableFacetedFormProps<T>) {
  const [dateError, setDateError] = React.useState<string | null>(null);
  const handleApply = (values: { [key: string]: string }) => {
    const selectedDates = options.filter((opt) => opt.mode === 'date');

    if (selectedDates.length === 2) {
      const startDate = values[selectedDates[0].value.toString()];
      const endDate = values[selectedDates[1].value.toString()];

      if (!startDate || !endDate) {
        setDateError('Both start date and end date are required.');
        return;
      }
    }

    setDateError(null);

    const result = options
      .map((option) => ({
        value: option.value,
        valValue: values[option.value.toString()],
      }))
      .filter((option) => option.valValue !== undefined && option.valValue !== '');

    onApply?.(result);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          className='flex w-full items-center gap-1 border-dashed px-2 sm:w-auto sm:flex-row'
        >
          <div className='flex items-center'>
            <PlusCircledIcon className='mr-2 h-4 w-4' />
            {title}
          </div>

          <div className='hidden items-center space-x-2 sm:flex'>
            {options.map((option) => (
              <React.Fragment key={option.value.toString()}>
                {option.valValue && (
                  <>
                    <Separator orientation='vertical' className='mx-2 h-4' />
                    <Badge variant='outline'>
                      {option.mode === 'rupiah'
                        ? formatIDR(option.valValue)
                        : option.mode === 'number'
                        ? option.valValue
                        : option.mode === 'date'
                        ? format(new Date(option.valValue), 'dd MMM yyyy')
                        : null}
                    </Badge>
                  </>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className='relative mt-1 flex items-center gap-2 sm:hidden'>
            {options.map((option) => (
              <React.Fragment key={option.value.toString()}>
                {option.valValue && (
                  <Badge variant='outline'>
                    {option.mode === 'rupiah'
                      ? formatIDR(option.valValue)
                      : option.mode === 'number'
                      ? option.valValue
                      : option.mode === 'date'
                      ? format(new Date(option.valValue), 'dd MMM yyyy')
                      : null}
                  </Badge>
                )}
              </React.Fragment>
            ))}
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent className='w-80 p-4'>
        <div className='grid gap-4'>
          <div className='space-y-2'>
            <h4 className='font-medium leading-none'>{title}</h4>
          </div>
          <Formik
            initialValues={options.reduce((acc, option) => {
              acc[option.value.toString()] = option.valValue || '';
              return acc;
            }, {} as { [key: string]: string })}
            onSubmit={handleApply}
          >
            {({ setFieldValue, values }) => (
              <Form className='grid gap-4'>
                {options.map((option) => (
                  <div className='grid grid-cols-1 gap-2' key={option.value}>
                    {option.mode === 'rupiah' ? (
                      <FormItem className='text-sm'>
                        <FormLabel htmlFor={option.value.toString()}>{option.label}</FormLabel>
                        <FormControl>
                          <Input
                            onChange={(e) => {
                              const userInput = e.target.value;
                              const numbersOnly = extractNumber(userInput);
                              setFieldValue(option.value.toString(), numbersOnly);
                            }}
                            value={
                              values[option.value]
                                ? formatCurrency(Number(values[option.value]))
                                : ''
                            }
                            name={option.value.toString()}
                            type='text'
                            leftContent='Rp'
                            placeholder='0'
                          />
                        </FormControl>
                      </FormItem>
                    ) : option.mode === 'number' ? (
                      <FormItem className='text-sm'>
                        <FormLabel htmlFor={option.value.toString()}>{option.label}</FormLabel>
                        <FormControl>
                          <Input
                            onChange={(e) => {
                              const userInput = e.target.value;
                              setFieldValue(option.value.toString(), userInput);
                            }}
                            value={values[option.value.toString()]}
                            name={option.value.toString()}
                            type='number'
                            placeholder='0'
                          />
                        </FormControl>
                      </FormItem>
                    ) : option.mode === 'date' ? (
                      <FormItem className='text-sm'>
                        <FormLabel htmlFor={option.value.toString()}>{option.label}</FormLabel>
                        <FormControl>
                          <DatePicker
                            placeholder='Select Date'
                            value={
                              values[option.value.toString()]
                                ? new Date(values[option.value.toString()])
                                : undefined
                            }
                            onChange={(val) => {
                              if (val) {
                                setFieldValue(option.value.toString(), val.toISOString());
                              } else {
                                setFieldValue(option.value.toString(), '');
                              }
                            }}
                            maxDate={new Date()}
                          />
                        </FormControl>
                      </FormItem>
                    ) : null}
                  </div>
                ))}
                {dateError && <p className='text-center text-xs text-red-600'>{dateError}</p>}
                <Button variant='default' type='submit' className='self-end'>
                  Apply
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </PopoverContent>
    </Popover>
  );
}
